/**
 * 쪽지 작성 모달 컴포넌트 (mailFormModal 패턴 미러 + lda note-form-modal 이식).
 *
 * 사용법:
 *   1) 페이지에 fragments/note-form-modal.html 을 include
 *        <div th:replace="~{fragments/note-form-modal :: modal}"></div>
 *   2) 트리거에서 이벤트 dispatch
 *        $dispatch('open-note-form')                                        — 일반 쓰기
 *        $dispatch('open-note-form', { replyTo: { id, name } })             — 답장(수신자 프리셀렉트)
 *        $dispatch('open-note-form', { linked: { conType, conIdx, label } })— 연계 발송
 *        (답장+연계 동시 전달 가능 — 연계 쪽지 답장 시 연계정보 유지)
 *
 * 연계 시 수신자 후보는 접근권한 필터(/note/api/recipients?conType=&conIdx= → data.users), 트리거가 넘긴 label 은 응답이 오기 전
 * 잠깐 보이는 힌트일 뿐 — 응답의 data.relLabel(발송 시 저장될 wrInfo3 와 동일 계산)로 즉시 교체한다(2026-09-16).
 * 소속(conContent1/2)도 발송 시점 서버가 재계산한다.
 * 발송 = POST /note/api/send (수신자당 1-row). 성공 시 reload.
 */
export default () => ({
    open: false,
    sending: false,
    loadingUsers: false,
    content: '',
    recipients: [],   // [{userid, name}] — 후보(본인 제외, 서버 필터)
    selected: [],     // 선택된 userid 목록
    sendToSelf: false,
    replyTo: null,    // { id, name } — 답장 대상
    linked: null,     // { conType, conIdx, label } — 연계 발송 컨텍스트
    recipientScope: '', // 서버가 알려준 주소록 범위('수금권한자' 등) — 빈 목록·하단 안내 문구용

    openModal(detail) {
        const d = detail || {};
        this.content = '';
        this.selected = [];
        this.sendToSelf = false;
        this.replyTo = d.replyTo || null;
        this.linked = (d.linked && d.linked.conType && d.linked.conType !== '0') ? d.linked : null;
        this.recipientScope = '';
        this.open = true;
        this.loadRecipients();
        this.$nextTick(() => this.$refs.noteContent?.focus());
    },

    close() {
        if (this.sending) return;
        this.open = false;
    },

    async loadRecipients() {
        this.loadingUsers = true;
        this.recipients = [];
        try {
            const q = this.linked
                ? '?conType=' + encodeURIComponent(this.linked.conType) + '&conIdx=' + encodeURIComponent(this.linked.conIdx)
                : '';
            const res = await fetch('/note/api/recipients' + q);
            const json = await res.json();
            if (json.success) {
                this.recipients = json.data?.users || [];
                this.recipientScope = json.data?.scope || '';
                // 서버 형식 연계 라벨('[종류] [(사건번호)|의뢰인명] [일자]')로 헤더 태그 교체 — 모달을 열 때부터 저장 라벨 그대로
                if (this.linked && json.data?.relLabel) {
                    this.linked = { ...this.linked, label: json.data.relLabel };
                }
                // 답장 — 발신자를 수신자로 프리셀렉트(후보에 없으면 — 본인 발신 등 — 내게쓰기)
                if (this.replyTo) {
                    if (this.recipients.some(u => u.userid === this.replyTo.id)) {
                        this.selected = [this.replyTo.id];
                    } else {
                        this.sendToSelf = true;
                    }
                }
            } else {
                this.$dispatch('show-toast', { type: 'error', title: '조회 실패', message: json.message });
            }
        } catch (e) {
            this.$dispatch('show-toast', { type: 'error', title: '오류', message: '수신자 조회 중 오류가 발생했습니다.' });
        } finally {
            this.loadingUsers = false;
        }
    },

    /** 빈 목록 안내 — 범위별 문구(수금권한자 필터에 '사무실에 다른 유저가 없습니다' 는 부정확, 2026-09-16). */
    get emptyMessage() {
        const s = this.recipientScope;
        return (!s || s === '사무실 유저') ? '사무실에 다른 유저가 없습니다.' : '수신 가능한 ' + s + '가 없습니다.';
    },

    /** 하단 안내 — 연계 발송 시 어떤 범위만 표시되는지. */
    get scopeNote() {
        const s = this.recipientScope;
        return (!s || s === '사무실 유저') ? '' : s + '만 표시됩니다.';
    },

    toggle(userid) {
        const i = this.selected.indexOf(userid);
        if (i >= 0) this.selected.splice(i, 1);
        else this.selected.push(userid);
    },

    get allChecked() {
        return this.recipients.length > 0 && this.selected.length === this.recipients.length;
    },

    toggleAll(checked) {
        this.selected = checked ? this.recipients.map(u => u.userid) : [];
    },

    get canSend() {
        return this.content.trim().length > 0 && (this.selected.length > 0 || this.sendToSelf);
    },

    async submit() {
        if (this.sending || !this.canSend) return;
        this.sending = true;
        try {
            const res = await fetch('/note/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: this.content.trim(),
                    receiverIds: this.selected,
                    sendToSelf: this.sendToSelf,
                    conType: this.linked ? this.linked.conType : '0',
                    conIdx: this.linked ? (this.linked.conIdx * 1 || 0) : 0
                })
            });
            const json = await res.json();
            if (json.success) {
                this.open = false;
                this.$dispatch('show-toast', {
                    type: 'success', title: '발송 완료',
                    message: (json.data?.sentCount || 0) + '건의 쪽지를 발송했습니다.'
                });
                setTimeout(() => location.reload(), 500);
            } else {
                this.$dispatch('show-toast', { type: 'error', title: '발송 실패', message: json.message });
            }
        } catch (e) {
            this.$dispatch('show-toast', { type: 'error', title: '오류', message: '발송 중 오류가 발생했습니다.' });
        } finally {
            this.sending = false;
        }
    }
});
